import React, { useState, useMemo } from 'react';
import Box from '../../atoms/Box';
import SearchToolbar from '../../molecules/SearchToolbar';
import DataTable, { DataTableColumn } from '../../molecules/DataTable';
import DefaultButton from '../../atoms/Buttons/DefaultButton';
import MuiFaIcon from '../../atoms/MuiFaIcon';
type SettingsContainerProps<TRow> = {
  data: TRow[];
  columns: DataTableColumn<TRow>[];
  onRowClick?: (row: TRow) => void;
  onAddClick?: () => void;
  addButtonLabel?: string;
  searchPlaceholder: string;
  noDataMessage?: string;
  /**
   * Optional accessor for searchable text. When provided, it takes
   * precedence over the default Object.values search.
   */
  searchAccessor?: (row: TRow) => string | string[];
  paginationOptions?: {
    rowsPerPageText?: string;
    rangeSeparatorText?: string;
  };
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  paginationState?: {
    currentPage: number;
    perPage: number;
  };
  onPaginationChange?: (state: {
    currentPage: number;
    perPage: number;
  }) => void;
  sortState?: {
    columnId?: string | number;
    direction?: 'asc' | 'desc';
  };
  onSortChange?: (state: {
    columnId?: string | number;
    direction?: 'asc' | 'desc';
  }) => void;
  /**
   * Forwarded to DataTable - persists sort + rows-per-page per user.
   * Do not combine with controlled sortState/paginationState.
   */
  persistKey?: string;
  /** false removes the horizontal overflow wrapper react-data-table adds. */
  responsive?: boolean;
};

export default function SettingsContainer<TRow extends { _id: string }>({
  data,
  columns,
  onRowClick,
  onAddClick,
  addButtonLabel = 'New',
  searchPlaceholder,
  noDataMessage = 'No items found',
  paginationOptions,
  searchValue,
  onSearchChange,
  paginationState,
  onPaginationChange,
  sortState,
  onSortChange,
  searchAccessor,
  persistKey,
  responsive,
}: SettingsContainerProps<TRow>) {
  const [searchTerm, setSearchTerm] = useState('');
  const actualSearchValue =
    searchValue !== undefined ? searchValue : searchTerm;
  const handleSearchChange = onSearchChange ?? setSearchTerm;

  const filteredData = useMemo(() => {
    if (!actualSearchValue) return data;

    const searchTerm = actualSearchValue.toLowerCase();

    return data.filter((item) => {
      if (searchAccessor) {
        const raw = searchAccessor(item);
        const values = Array.isArray(raw) ? raw : [raw];
        return values.some(
          (val) =>
            typeof val === 'string' && val.toLowerCase().includes(searchTerm)
        );
      }

      return Object.values(item).some(
        (val) =>
          typeof val === 'string' && val.toLowerCase().includes(searchTerm)
      );
    });
  }, [data, actualSearchValue, searchAccessor]);

  return (
    <Box sx={{ mt: 2 }}>
      <SearchToolbar
        searchValue={actualSearchValue}
        onSearchChange={handleSearchChange}
        searchPlaceholder={searchPlaceholder}
        actions={
          onAddClick && (
            <DefaultButton
              icon={<MuiFaIcon name="fa-solid fa-plus" />}
              variant="contained"
              disableElevation
              sx={{ textTransform: 'none' }}
              onClick={onAddClick}
            >
              {addButtonLabel}
            </DefaultButton>
          )
        }
      />

      <DataTable
        columns={columns}
        data={filteredData}
        persistKey={persistKey}
        responsive={responsive}
        onRowClick={onRowClick}
        paginationComponentOptions={paginationOptions}
        paginationDefaultPage={paginationState?.currentPage}
        paginationPerPage={paginationState?.perPage}
        onChangePage={(page) => {
          if (onPaginationChange) {
            onPaginationChange({
              ...(paginationState ?? { currentPage: page, perPage: 10 }),
              currentPage: page,
            });
          }
        }}
        onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
          if (onPaginationChange) {
            onPaginationChange({
              ...(paginationState ?? {
                currentPage,
                perPage: currentRowsPerPage,
              }),
              currentPage,
              perPage: currentRowsPerPage,
            });
          }
        }}
        defaultSortFieldId={sortState?.columnId}
        defaultSortAsc={sortState?.direction !== 'desc'}
        sortServer={Boolean(onSortChange)}
        onSort={(column, direction) => {
          if (onSortChange) {
            onSortChange({ columnId: column?.id, direction });
          }
        }}
        noDataComponent={
          <Box sx={{ py: 4, textAlign: 'center' }}>{noDataMessage}</Box>
        }
      />
    </Box>
  );
}
